import { Timestamp, Query, query, where, orderBy } from 'firebase/firestore';
import { IFutureOrder, FutureOrderExposure } from '../sp-types/FutureOrderType';
import { deleteItemsByIds, getItems, saveItem } from './genericFB';

const FUTURE_ORDER_EXPOSURE = 'future-orders-exposure';

export const deleteFutureExposureByOrderId = async (accountId: string, orderId: string): Promise<any> => {
  const exposures = await getItems<FutureOrderExposure>(accountId, FUTURE_ORDER_EXPOSURE, (q: Query) =>
    query(q, where('orderId', '==', orderId)),
  );
  if (exposures && exposures.length > 0) {
    return await deleteItemsByIds(
      exposures.map((e) => e.uuid),
      FUTURE_ORDER_EXPOSURE,
    );
  }
  return null;
};

export const saveFutureOrderExposures = async ({
  accountId,
  uuid,
  monthExposure,
}: IFutureOrder): Promise<(FutureOrderExposure | null)[] | null> => {
  if (uuid && accountId && monthExposure) {
    // First, delete any existing exposure records for this order
    // This ensures that if months are removed from the order, the old records are cleaned up
    await deleteFutureExposureByOrderId(accountId, uuid);

    const exposureRecords = Object.entries(monthExposure || {}).flatMap(([materialId, exposureCalendar]) =>
      (exposureCalendar || []).map(
        ({ date: exposeMonth, value: exposureValue }) =>
          ({
            uuid: `${accountId}_${uuid}_${materialId}_${exposeMonth.year}-${exposeMonth.month}`,
            orderId: uuid,
            accountId,
            materialId,
            exposeMonth,
            exposureValue,
            exposureDate: Timestamp.fromDate(new Date(exposeMonth.year, exposeMonth.month - 1, 1)),
          } as FutureOrderExposure),
      ),
    );

    const results = await Promise.all(
      exposureRecords.map((record) => saveItem<FutureOrderExposure>(record.uuid, record, FUTURE_ORDER_EXPOSURE)),
    );

    return results.filter(Boolean);
  }

  return null;
};

export const getFutureExposure = async (
  accountId: string,
  fromDate?: Date,
): Promise<FutureOrderExposure[] | undefined> => {
  return getItems<FutureOrderExposure>(accountId, FUTURE_ORDER_EXPOSURE, (q: Query) =>
    query(
      q,
      fromDate ? where('exposureDate', '>=', Timestamp.fromDate(fromDate)) : where('accountId', '==', accountId),
      orderBy('exposureDate', 'asc'),
    ),
  );
};

export const deleteFutureExposure = async (accountId: string): Promise<any> => {
  const orders = await getItems<FutureOrderExposure>(accountId, FUTURE_ORDER_EXPOSURE, (q: Query) =>
    query(q, where('accountId', '==', accountId)),
  );
  return await deleteItemsByIds(orders?.map((a) => a.orderId) || [], FUTURE_ORDER_EXPOSURE);
};
